import { AuthResponse } from "./types";

const BASE_URL = "https://prod-api-v2.chlk.football";

const KEYS = {
  ACCESS: "chlk_access_token",
  REFRESH: "chlk_refresh_token",
  EXPIRES_AT: "chlk_expires_at",
};

let refreshInFlight: Promise<string> | null = null;
let officeStorageUnavailable = false;
const STORAGE_TIMEOUT_MS = 3_000;

export class AuthExpiredError extends Error {
  status = 401;
  constructor() {
    super("Session expired");
  }
}

function localStorageAdapter() {
  return {
    getItems: async (keys: string[]) => {
      const result: Record<string, string | null> = {};
      keys.forEach((k) => { result[k] = localStorage.getItem(k); });
      return result;
    },
    setItems: async (items: Record<string, string>) => {
      Object.entries(items).forEach(([k, v]) => localStorage.setItem(k, v));
    },
    removeItems: async (keys: string[]) => {
      keys.forEach((k) => localStorage.removeItem(k));
    },
  };
}

async function withStorageFallback<T>(
  operation: (target: ReturnType<typeof localStorageAdapter>) => Promise<T>
): Promise<T> {
  const fallback = localStorageAdapter();
  if (
    officeStorageUnavailable ||
    typeof OfficeRuntime === "undefined" ||
    !OfficeRuntime.storage
  ) {
    return operation(fallback);
  }

  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      operation(OfficeRuntime.storage),
      new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error("OfficeRuntime.storage timed out")),
          STORAGE_TIMEOUT_MS
        );
      }),
    ]);
  } catch (error) {
    officeStorageUnavailable = true;
    console.error("[CHLK storage fallback]", error);
    return operation(fallback);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export async function getStoredTokens(): Promise<StoredTokens | null> {
  const items = await withStorageFallback((target) =>
    target.getItems([KEYS.ACCESS, KEYS.REFRESH, KEYS.EXPIRES_AT])
  );
  const access = items[KEYS.ACCESS];
  const refresh = items[KEYS.REFRESH];
  const expiresAt = items[KEYS.EXPIRES_AT];
  if (!access || !refresh || !expiresAt) return null;
  return { accessToken: access, refreshToken: refresh, expiresAt: Number(expiresAt) };
}

export async function saveTokens(resp: AuthResponse): Promise<void> {
  // Normalize expiresAt to milliseconds. API may return seconds or ms.
  const rawAt = resp.expiresAt;
  const expiresAtMs = rawAt > 1e12 ? rawAt : rawAt * 1000;
  await withStorageFallback((target) => target.setItems({
    [KEYS.ACCESS]: resp.accessToken,
    [KEYS.REFRESH]: resp.refreshToken,
    [KEYS.EXPIRES_AT]: String(expiresAtMs),
  }));
}

export async function clearTokens(): Promise<void> {
  await withStorageFallback((target) =>
    target.removeItems([KEYS.ACCESS, KEYS.REFRESH, KEYS.EXPIRES_AT])
  );
}

async function doRefresh(refreshToken: string): Promise<AuthResponse> {
  const resp = await fetch(
    `${BASE_URL}/auth/refresh-token/${encodeURIComponent(refreshToken)}`,
    { method: "POST" }
  );
  if (!resp.ok) throw new AuthExpiredError();
  return resp.json();
}

function refreshWithLock(refreshToken: string): Promise<string> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const refreshed = await doRefresh(refreshToken);
      await saveTokens(refreshed);
      return refreshed.accessToken;
    } catch {
      await clearTokens();
      throw new AuthExpiredError();
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function getValidAccessToken(): Promise<string> {
  const tokens = await getStoredTokens();
  if (!tokens) throw new AuthExpiredError();

  // Proactively refresh if expiring within 60 seconds
  if (tokens.expiresAt - 60_000 <= Date.now()) {
    return refreshWithLock(tokens.refreshToken);
  }

  return tokens.accessToken;
}

export async function refreshAccessToken(): Promise<string> {
  const tokens = await getStoredTokens();
  if (!tokens) throw new AuthExpiredError();
  return refreshWithLock(tokens.refreshToken);
}

export async function login(email: string, password: string): Promise<void> {
  const resp = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!resp.ok) {
    const err: { status: number } = { status: resp.status };
    throw err;
  }
  const data: AuthResponse = await resp.json();
  await saveTokens(data);
}

export interface SocialSignInError {
  status: number;
  code?: string;
  message?: string;
}

export async function signInWithGoogle(
  idToken: string,
  accessToken?: string,
  firstName?: string,
  lastName?: string
): Promise<void> {
  const body: Record<string, string | undefined> = {
    idToken,
    accessToken,
    firstName,
    lastName,
  };
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  const resp = await fetch(`${BASE_URL}/auth/sign-in-with-google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    const err: SocialSignInError = {
      status: resp.status,
      code: data.code,
      message: data.message || data.detail,
    };
    throw err;
  }

  const data: AuthResponse = await resp.json();
  await saveTokens(data);
}

export async function signInWithApple(
  idToken: string,
  nonce?: string,
  firstName?: string,
  lastName?: string
): Promise<void> {
  const body: Record<string, string | undefined> = {
    idToken,
    nonce,
    firstName,
    lastName,
  };
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  const resp = await fetch(`${BASE_URL}/auth/sign-in-with-apple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    const err: SocialSignInError = {
      status: resp.status,
      code: data.code,
      message: data.message || data.detail,
    };
    throw err;
  }

  const data: AuthResponse = await resp.json();
  await saveTokens(data);
}
